import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import classNames from "classnames";
import { memo } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { WidthValue } from "../../theme/box";
import { RedoBadge, RedoBadgeColor } from "../badge/redo-badge";
import * as orderSummaryCss from "./redo-order-summary.module.css";

export interface RedoOrderSummaryProps {
  orderNumber: string;
  orderDate: string;
  itemCount: number;
  totalPrice: string;
  financialStatus: string;
  imageUrl?: string;
  onClick?: () => void;
  className?: string;
  w?: WidthValue;
}

// Function to get badge color based on financial status
function getFinancialStatusBadgeColor(status: string): RedoBadgeColor {
  switch (status.toLowerCase()) {
    case "paid":
      return "success";
    case "authorized":
      return "blue";
    case "partially_paid":
    case "partially paid":
      return "warning";
    case "partially_refunded":
    case "partially refunded":
      return "orange";
    case "pending":
      return "gray";
    case "refunded":
      return "purple";
    case "voided":
      return "error";
    default:
      return "gray";
  }
}

// Function to format financial status display text
function formatFinancialStatus(status: string): string {
  switch (status.toLowerCase()) {
    case "partially_paid":
      return "Partially paid";
    case "partially_refunded":
      return "Partially refunded";
    default:
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }
}

export const RedoOrderSummary = memo(function RedoOrderSummary({
  orderNumber,
  orderDate,
  itemCount,
  totalPrice,
  financialStatus,
  imageUrl,
  onClick,
  className,
  w,
}: RedoOrderSummaryProps) {
  return (
    <Flex
      align="center"
      borderStyle="solid"
      borderWidth="1px"
      className={classNames(
        orderSummaryCss.orderSummary,
        "cursor-pointer",
        className,
      )}
      dir="row"
      gap="md"
      onClick={onClick}
      p="lg"
      radius="md"
      w={w}
    >
      {imageUrl && (
        <img
          alt="Order item"
          className={orderSummaryCss.orderImage}
          src={imageUrl}
          style={{ width: "56px", height: "56px" }}
        />
      )}

      <Flex
        className={orderSummaryCss.orderDetails}
        dir="column"
        gap="xs"
        grow={1}
      >
        <Text fontSize="sm" fontWeight="medium" textColor="primary">
          Order {orderNumber}
        </Text>
        <Text fontSize="xs" fontWeight="thin" textColor="tertiary">
          {orderDate} <span style={{ opacity: 0.6 }}>|</span> {itemCount} item
          {itemCount !== 1 ? "s" : ""}
        </Text>
        <Flex align="center" dir="row" gap="xs">
          <Text
            className={orderSummaryCss.priceText}
            fontSize="xs"
            fontWeight="regular"
          >
            ${parseFloat(totalPrice).toFixed(2)}
          </Text>
          <RedoBadge
            color={getFinancialStatusBadgeColor(financialStatus)}
            size="xs"
            text={formatFinancialStatus(financialStatus)}
          />
        </Flex>
      </Flex>

      <ChevronRightIcon className={orderSummaryCss.rightArrow} />
    </Flex>
  );
});
